import { Link } from "react-router-dom";
import { ChevronRight, ArrowUpRight, MapPin, Instagram } from "lucide-react";
import HeroCarousel from "@/components/site/HeroCarousel";
import StatsCounter from "@/components/site/StatsCounter";
import SectionHeading from "@/components/site/SectionHeading";
import ProgramCard from "@/components/site/ProgramCard";
import { STATS, PROGRAMS, BRANCHES } from "@/lib/data";

export default function Home() {
  // Show only 4 program cards on the home page
  const featuredPrograms = PROGRAMS.slice(0, 4);

  return (
    <div data-testid="home-page">
      <HeroCarousel />

      {/* Marquee */}
      <div className="bg-[#ff4500] py-3 overflow-hidden border-y border-black/20">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6">
              {["Chennai", "Sacramento", "Kitchener", "Hip-Hop", "Contemporary", "Bollywood", "Salsa", "Kids", "Online", "Wedding"].map((w, i) => (
                <span key={i} className="font-display text-2xl tracking-wider text-black">
                  ◆ {w}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Brand statement */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Welcome to Phoenix</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              One academy.<br />
              <span className="phoenix-text">Three countries.</span><br />
              Endless stages.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-base lg:text-lg text-white/75 leading-relaxed">
              Phoenix Dance Academy is a modern international dance institution training kids, teens, adults, beginners, and professionals across Chennai, Sacramento, and Kitchener.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about">
                <button data-testid="home-about-btn" className="border border-white/25 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2">
                  Read our story <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/instructors">
                <button data-testid="home-instructors-btn" className="border border-white/25 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2">
                  Meet the faculty <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <StatsCounter stats={STATS} />

      {/* Programs preview — short */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <SectionHeading
            eyebrow="Programs"
            title="Find your"
            accent="program."
            subtitle="A snapshot of our most-loved programs. The full catalogue lives on the Classes page."
            testid="programs-heading"
          />
          <Link to="/classes">
            <button data-testid="view-all-classes-btn" className="border border-white/30 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors whitespace-nowrap">
              View All Classes →
            </button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6">
          {featuredPrograms.map((p) => (
            <ProgramCard key={p.slug} program={p} />
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-24 lg:py-28 overflow-hidden">
        <img src="https://images.pexels.com/photos/3022420/pexels-photo-3022420.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-5">◆ Ready to begin?</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              Ready to start your <span className="phoenix-text">dance journey?</span>
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book-trial">
                <button data-testid="cta-book-trial-btn" className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2">
                  Book Free Trial <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/contact">
                <button data-testid="cta-contact-btn" className="border border-white/30 text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold hover:bg-white hover:text-black transition-colors">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Locations preview — compact */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <SectionHeading eyebrow="Branches" title="Train across" accent="three countries." testid="locations-heading" />
          <Link to="/contact">
            <button data-testid="all-locations-btn" className="border border-white/30 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors whitespace-nowrap">
              All Branch Details →
            </button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
          {BRANCHES.map((b) => (
            <div
              key={b.id}
              data-testid={`branch-${b.id}`}
              className="group bg-[#0a0a0a] p-7 hover:bg-[#0f0f0f] transition-colors"
            >
              <div className="text-4xl mb-3">{b.flag}</div>
              <h3 className="font-display text-2xl text-white">{b.city}</h3>
              <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mt-1">{b.name}</p>
              <div className="mt-4 space-y-3">
                {b.locations.map((loc) => (
                  <div key={loc.id} className="text-sm text-white/55">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{loc.name}</p>
                    <p className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-white/40" /> {loc.address}
                    </p>
                    <a
                      href={`https://instagram.com/${loc.instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`home-ig-${loc.id}`}
                      className="mt-1.5 ml-6 inline-flex items-center gap-1.5 text-xs text-[#ffb700] hover:text-[#ff4500] transition-colors"
                    >
                      <Instagram className="w-3 h-3" /> @{loc.instagram}
                    </a>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#ff4500] font-semibold group-hover:gap-3 transition-all">
                Visit details <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
